<i18n>
{
	"en": {
		"header": "Trip Templates",
		"deleteTemplate": "Delete Trip Template | Delete Trip Template | Delete {count} Trip Templates",
		"newTemplate": "New Trip Template",
		"noTemplates": "No Trip Templates Found",
		"name": "Name",
		"namePlaceholder": "New template",
		"duration": "Duration",
		"days": "Day | Days",
		"type": "Type",
		"linehaul": "Linehaul",
		"external": "External",
		"distribution": "Distribution",
		"fromAddress": "From",
		"toAddress": "To",
		"driver": "Driver",
		"vendor": "Owner",
		"clerk": "Clerk",
		"failedToFetch": "Failed to fetch trip templates",
		"failedToDelete": "Failed to delete trip template",
		"updatedValue": "Successfully updated trip template",
		"editTemplate": "Edit Template",
		"autoSchedule": "Auto",
		"nextTrip": "Next generated trip",
		"status": "Status",
		"generateTrips": "Generate Trips...",
		"yes": "Yes",
		"no": "No"
	},
	"no": {
		"header": "Turmaler",
		"deleteTemplate": "Slett Turmal | Slett Turmal | Slett {count} Turmaler",
		"newTemplate": "Ny Turmal",
		"noTemplates": "Fant Ingen Turmaler",
		"name": "Navn",
		"namePlaceholder": "Ny mal",
		"duration": "Varighet",
		"days": "Dag | Dager",
		"type": "Type",
		"linehaul": "Langtransport",
		"external": "Ekstern",
		"distribution": "Distribusjon",
		"fromAddress": "Fra",
		"toAddress": "To",
		"driver": "Sjåfør",
		"vendor": "Eier",
		"clerk": "Ansvarlig",
		"failedToFetch": "Kunne ikke hente turmaler",
		"failedToDelete": "Kunne ikke slette turmal",
		"updatedValue": "Turmalen ble oppdatert",
		"editTemplate": "Rediger Turmal",
		"autoSchedule": "Auto",
		"nextTrip": "Neste genererte tur",
		"status": "Status",
		"generateTrips": "Generer Turer...",
		"yes": "Ja",
		"no": "Nei"
	}
}
</i18n>

<template>
	<t-vertical-panel class="trip-templates">
		<template #header>
			<div class="trip-templates-header">
				<t-panel-header size="md" icon="trip">
					{{ $t('header') }}
				</t-panel-header>
			</div>
		</template>
		<template #sub-header>
			<div class="trip-templates-sub-header">
				<div class="button-group">
					<t-button
						:disabled="rowsSelected !== 1"
						iconBefore="annotation"
						appearance="minimal"
						@click="editTemplate()"
					>{{ $t('editTemplate') }}</t-button>
					<t-button
						iconBefore="add-to-artifact"
						@click="autoScheduleTrips"
					>{{ $t('generateTrips') }}</t-button>
				</div>
				<div class="button-group right">
					<t-button
						:disabled="rowsSelected === 0"
						iconBefore="trash"
						appearance="minimal"
						@click="deleteTemplate"
					>{{ $tc('deleteTemplate', rowsSelected) }}</t-button>
					<t-button
						iconBefore="plus"
						appearance="primary"
						@click="newTemplate"
					>{{ $t('newTemplate') }}</t-button>
				</div>
			</div>
		</template>
		<div class="trip-templates-content">
			<t-grid
				v-if="tripTemplates.length"
				ref="grid"
				:columnDefinitions="columnDefinitions"
				:defaultColumnDefinition="defaultColumnDefinition"
				:rowHeight="50"
				:items="tripTemplates"
				selectable
				@selected="onRowsSelected"
				@rowDoubleClick="editTemplate"
				@enter="editTemplate()"
			>
				<template #duration="{ item }">
					{{ item.duration }} {{ $tc('days', item.duration) }}
				</template>
				<template #autoSchedule="{ item }">
					{{ item.autoSchedule ? $t('yes') : $t('no') }}
				</template>
				<template #status="{ item }">
					<div v-if="item.cargoCarrier.cargoCarrierType && item.cargoCarrier.cargoCarrierType.id !== undefined">
						<t-timpex-icon
							:icon="getCargoCarrierIcon(
								item.cargoCarrier.cargoCarrierType
									&& item.cargoCarrier.cargoCarrierType.iconId
							)"
							:color="getStatusColor(item.status)"
							size="md"
						/>
						<div>
							{{ item.cargoCarrier.registrationNumber }}
						</div>
					</div>
					<div v-else>
						<t-timpex-icon
							:color="getStatusColor(item.status)"
							icon="unknown-vehicle"
							type="bp"
						/>
					</div>
				</template>
				<template #type="{ item }">
					<t-timpex-icon
						v-if="!item.type.internalCode"
						icon="flows"
						type="bp"
						color="orange"
					/>
					<t-timpex-icon
						v-else
						:icon="getTripTypeIcon(item.type.internalCode)"
						type="bp"
						color="neutral"
					/>
				</template>
				<template #fromAddress="{ item }">
					<div v-if="item.type.internalCode === 'external'">
						{{ `Via ${item.vendor.name}` }}
					</div>
					<div v-else>
						<div style="height: 16px;">{{ item.fromAddress.name }}</div>
						<div class="grid-cell-sub-line">{{ item.fromAddress.street }} {{ item.fromAddress.postalCode }} {{ item.fromAddress.postalPlace }}</div>
					</div>
				</template>
				<template #toAddress="{ item }">
					<div v-if="item.type.internalCode === 'external'">
						{{ `Via ${item.vendor.name}` }}
					</div>
					<div v-else>
						<div style="height: 16px;">{{ item.toAddress.name }}</div>
						<div class="grid-cell-sub-line">{{ item.toAddress.street }} {{ item.toAddress.postalCode }} {{ item.toAddress.postalPlace }}</div>
					</div>
				</template>
			</t-grid>

			<t-no-content-panel v-else :text="$t('noTemplates')" icon="trip" />
			<t-overlay
				:isShown="loading"
				absolute
				loader
			/>
		</div>

		<auto-schedule-dialog
			:isShown="showAutoScheduleDialog"
			:selectedTemplates="selectedTemplates"
			@close="autoScheduleClosed"
			@ok="getTripTemplates(true)"
		/>
	</t-vertical-panel>
</template>

<script>
import { tripTypeIcon, cargoCarrierIcon, statuses, eventBus, displayDateFormatter } from '@/core';
import { mapState, mapActions } from './store/tripTemplates.store';
import AutoScheduleDialog from './AutoScheduleDialog.vue';

export default {
	name: 'TripTemplates',
	components: { AutoScheduleDialog },
	data() {
		return {
			defaultColumnDefinition: {
				resizable: true,
				headerFilterInputWithButton: true,
			},
			rowsSelected: 0,
			selectedTemplates: [],
			showAutoScheduleDialog: false
		};
	},
	computed: {
		...mapState([
			'tripTemplates',
			'loading'
		]),
		columnDefinitions() {
			return [
				{
					field: 'name',
					displayName: this.$t('name'),
					width: 150
				},
				{
					field: 'duration',
					displayName: this.$t('duration'),
					width: 70,
					align: 'center'
				},
				{
					field: 'autoSchedule',
					displayName: this.$t('autoSchedule'),
					valueGetter: (item) => item.autoSchedule,
					filterable: false,
					align: 'center',
					width: 50
				},
				{
					field: 'status',
					displayName: this.$t('status'),
					valueGetter: (item) => item.status,
					resizable: false,
					width: 100,
					align: 'center',
				},
				{
					field: 'type',
					displayName: this.$t('type'),
					valueGetter: (item) => item.type.description,
					width: 53,
					align: 'center',
				},
				{
					field: 'clerk',
					displayName: this.$t('clerk'),
					minWidth: 100,
					valueGetter: (item) => item.clerk && 'name' in item.clerk ? item.clerk.name : ''
				},
				{
					field: 'fromAddress',
					displayName: this.$t('fromAddress'),
					valueGetter: (item) => {
						item.type.internalCode === 'external' ? `Via ${item.vendor.name}` : `${item.fromAddress.name} ${item.fromAddress.street} ${item.fromAddress.postalCode} ${item.fromAddress.postalPlace}`;
					},
					minWidth: 150
				},
				{
					field: 'toAddress',
					displayName: this.$t('toAddress'),
					valueGetter: (item) => {
						item.type.internalCode === 'external' ? `Via ${item.vendor.name}` : `${item.toAddress.name} ${item.toAddress.street} ${item.toAddress.postalCode} ${item.toAddress.postalPlace}`;
					},
					minWidth: 150,
				},
				{
					field: 'vendor',
					displayName: this.$t('vendor'),
					valueGetter: (item) => item.vendor && 'name' in item.vendor ? `${item.vendor.name} (${item.vendor.vendorNumber})` : '',
					minWidth: 100,
				},
				{
					field: 'driver',
					displayName: this.$t('driver'),
					valueGetter: (item) => item.driver && 'name' in item.driver ? item.driver.name : '',
					minWidth: 100,
				},
				{
					field: 'nextTrip',
					displayName: this.$t('nextTrip'),
					valueGetter: (item) => item.nextTrip && item.autoSchedule ? this.displayDate(item.nextTrip) : '',
					align: 'center',
					width: 140,
				}
			];
		}
	},
	async mounted() {
		try {
			await this.getTripTemplates();
		} catch (e) {
			this.$notify({
				type: 'error',
				title: this.$t(e.message)
			});
		}
	},
	methods: {
		...mapActions([
			'getTripTemplates',
			'deleteTemplates',
		]),
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
		},
		getTripTypeIcon(iconId) {
			return tripTypeIcon(iconId);
		},
		getCargoCarrierIcon(iconId) {
			return cargoCarrierIcon.getIcon(iconId);
		},
		getStatusColor(status) {
			return statuses.color(status);
		},
		displayDate(storageDate) {
			return displayDateFormatter(storageDate);
		},
		newTemplate() {
			eventBus.$emit('toggleTripTemplateDetailsModal', true);
		},
		editTemplate(id) {
			let template = undefined;
			if (id) {
				template = this.tripTemplates.find( (x) => x.id === id );
			} else {
				let selectedItems = this.$refs.grid.getSelectedItems();
				if (selectedItems.length > 1) return;
				[template] = selectedItems;
			}
			eventBus.$emit('setTripTemplateDetails', template);
			eventBus.$emit('toggleTripTemplateDetailsModal', true);
		},
		autoScheduleTrips() {
			this.selectedTemplates = this.$refs.grid.getSelectedItems();
			this.showAutoScheduleDialog = true;
		},
		autoScheduleClosed() {
			this.selectedTemplates = [];
			this.showAutoScheduleDialog = false;
		},
		async deleteTemplate() {
			let items = this.$refs.grid.getSelectedItems().map(x => x.id);
			try {
				await this.deleteTemplates(items);
				this.rowsSelected = 0;
				if (this.$refs.grid) this.$refs.grid.resetSelectedRows();
			} catch (err) {
				this.$notify({
					type: 'error',
					title: this.$t(err.message)
				});
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.trip-templates {
	.trip-templates-header {
		height: 100%;
		display: flex;
		align-items: center;
		padding-left: 15px;
	}
	.trip-templates-sub-header {
		display: flex;
		padding: 0px 15px;
		height: 100%;
		align-items: center;
		justify-content: space-between;

		.button-group {
			display: flex;
			height: 100%;
			align-items: center;
			justify-content: flex-end;
			> * {
				margin-right: 10px;
			}
			&.right > * {
				margin-right: 0px;
				margin-left: 10px;
			}
		}
	}
	.trip-templates-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px;
	}
}
</style>

